/* global setTimeout, clearTimeout, WebSocket */

import { RunEvents, RunState } from "./asyncRun";

/**
 * The run status pushes, as a wait listens to them (#716, "Async runs: push the run's
 * status to the pane over Web PubSub").
 *
 * cnp-service's queue worker sends `{runId, status}` to the user through Web PubSub, and
 * the middle tier's run-events gives the pane a client URL for it. A native WebSocket,
 * receive-only, with no subprotocol: Web PubSub sends a server message to such a client
 * as its plain text. No SDK: @azure/web-pubsub-client needs TypeScript 5 and Safari 16,
 * and reconnects without end.
 *
 * A push only says "read now" (asyncRun.waitForRun); a lost one costs at most the
 * backstop. A socket that cannot open within CONNECT_TIMEOUT_MS, or closes, is tried
 * again after 1, 2 and 4 s, each time with a new URL, three times in all for a wait; after that, or when run-events
 * refuses, the source stays closed and the wait polls as without it. Pure on purpose:
 * the URL, the WebSocket and the timers are given, so tests run it without a browser
 * (tests/ui/specs/run-events.unit.spec.ts).
 *
 * Nothing here is logged: the URL carries the token, and a socket's error event holds
 * the URL. `onClosedForGood` gets a fixed reason.
 */

/** How long a socket may take to open before it counts as a failed attempt. */
export const CONNECT_TIMEOUT_MS = 5 * 1000;
/** The pauses before each new attempt after a failed or closed socket. */
export const RECONNECT_DELAYS_MS = [1000, 2000, 4000];

const STATES: ReadonlySet<string> = new Set(["queued", "running", "done", "failed", "superseded"]);

/** Why a source stopped for good: run-events said no, or the socket kept failing. */
export type ClosedReason = "refused" | "unreachable" | "dropped";

/** What the source uses of a WebSocket: the browser's, or a test's. */
export interface SocketLike {
  onopen: ((event: any) => void) | null;
  onmessage: ((event: any) => void) | null;
  onclose: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  close(): void;
}

export interface ConnectOptions {
  /** A client URL from run-events; rejects when run-events refuses or cannot be reached. */
  clientUrl: () => Promise<string>;
  /** Opens a socket: `new WebSocket(url)` in the pane. */
  open?: (url: string) => SocketLike;
  setTimer?: (callback: () => void, ms: number) => unknown;
  clearTimer?: (timer: unknown) => void;
  onClosedForGood?: (reason: ClosedReason) => void;
}

export interface RunEventSource extends RunEvents {
  /** Stops listening for good: the socket closes, and nothing is tried again. */
  close(): void;
}

/** A push as Web PubSub delivers it: `{runId, status}` as text; anything else is ignored. */
export function parsePush(data: unknown): { runId: string; status: RunState } | undefined {
  if (typeof data !== "string") return undefined;
  try {
    const push = JSON.parse(data);
    if (push && typeof push.runId === "string" && push.runId && STATES.has(push.status)) {
      return { runId: push.runId, status: push.status };
    }
  } catch {
    // Not a push of ours.
  }
  return undefined;
}

export function connectRunEvents({
  clientUrl,
  open = (url) => new WebSocket(url),
  setTimer = (callback, ms) => setTimeout(callback, ms),
  clearTimer = (timer) => clearTimeout(timer as ReturnType<typeof setTimeout>),
  onClosedForGood = () => undefined,
}: ConnectOptions): RunEventSource {
  let socket: SocketLike | undefined;
  let socketOpen = false;
  let opens = 0;
  let stopped = false;
  let attempts = 0;
  let connectTimer: unknown;
  let reconnectTimer: unknown;
  const pushed = new Map<string, RunState>();
  const waiters = new Set<() => void>();

  const wake = () => {
    const woken = [...waiters];
    waiters.clear();
    woken.forEach((resolve) => resolve());
  };

  const stopForGood = (reason: ClosedReason) => {
    if (stopped) return;
    stopped = true;
    socketOpen = false;
    wake();
    onClosedForGood(reason);
  };

  const dropSocket = () => {
    if (connectTimer !== undefined) clearTimer(connectTimer);
    connectTimer = undefined;
    const old = socket;
    socket = undefined;
    if (old) {
      old.onopen = old.onmessage = old.onclose = old.onerror = null;
      try {
        old.close();
      } catch {
        // Already closed.
      }
    }
  };

  const retry = (reason: ClosedReason) => {
    const wasOpen = socketOpen;
    socketOpen = false;
    dropSocket();
    if (stopped) return;
    if (wasOpen) wake();
    if (attempts >= RECONNECT_DELAYS_MS.length) {
      stopForGood(reason);
      return;
    }
    const delay = RECONNECT_DELAYS_MS[attempts];
    attempts += 1;
    reconnectTimer = setTimer(() => {
      reconnectTimer = undefined;
      void connect();
    }, delay);
  };

  const connect = async () => {
    if (stopped) return;
    let url: string;
    try {
      url = await clientUrl();
    } catch {
      // run-events refused (events off, a limit, a token it did not take) or was not
      // reached: the wait polls, and asking again would not change it soon.
      stopForGood("refused");
      return;
    }
    if (stopped) return;
    let opened: SocketLike;
    try {
      opened = open(url);
    } catch {
      retry("unreachable");
      return;
    }
    socket = opened;
    connectTimer = setTimer(() => {
      if (socket === opened && !socketOpen) retry("unreachable");
    }, CONNECT_TIMEOUT_MS);
    opened.onopen = () => {
      if (socket !== opened || stopped) return;
      if (connectTimer !== undefined) clearTimer(connectTimer);
      connectTimer = undefined;
      socketOpen = true;
      opens += 1;
      wake();
    };
    opened.onmessage = (event) => {
      if (socket !== opened) return;
      const push = parsePush(event?.data);
      if (!push) return;
      pushed.set(push.runId, push.status);
      wake();
    };
    // An error is followed by a close; either ends this socket, once.
    opened.onclose = opened.onerror = () => {
      if (socket === opened) retry(socketOpen ? "dropped" : "unreachable");
    };
  };

  void connect();

  return {
    isOpen: () => socketOpen && !stopped,
    opens: () => opens,
    take: (runId) => {
      const status = pushed.get(runId);
      pushed.delete(runId);
      return status;
    },
    changed: (ms) =>
      new Promise<void>((resolve) => {
        if (stopped && !socketOpen) {
          // Nothing will ever change: the wait's own pause.
          setTimer(resolve, ms);
          return;
        }
        const finish = () => {
          waiters.delete(finish);
          resolve();
        };
        waiters.add(finish);
        setTimer(finish, ms);
      }),
    close: () => {
      if (stopped) return;
      stopped = true;
      socketOpen = false;
      if (reconnectTimer !== undefined) clearTimer(reconnectTimer);
      reconnectTimer = undefined;
      dropSocket();
      wake();
    },
  };
}
